import type { ReactNode } from 'react';
import { cx } from '../cx';
import type { ChartLayout } from './layout';

export type RingProps = {
  /** The reader's share, 0 to 100. */
  percent: number;
  /** The number inside, formatted by the page ("72%"). */
  display: ReactNode;
  /** What it is ("Your match"). */
  label: ReactNode;
  /** The count in words ("8 of 11 requirements on your CV"). */
  detail?: ReactNode;
  /** `narrow` puts the words centred below the ring instead of beside it. `auto` (default) keeps them beside it: a Ring sizes to its content, so it has no container width to switch on. */
  layout?: ChartLayout;
  className?: string;
};

const R = 34;
const CIRCUMFERENCE = 2 * Math.PI * R;

/**
 * The one ring the system allows: a single share that belongs to the reader (your match, profile
 * complete), in ink, with the number inside and the count in words beside it. Never for market data.
 */
// A Ring sizes itself to its content, so it cannot be its own size container (it would measure
// zero inside a flex row). `auto` therefore keeps the words beside the ring; ask for `narrow` to stack.
const ROOT: Record<ChartLayout, string> = {
  auto: 'flex items-center gap-3.5',
  narrow: 'flex flex-col items-center gap-2 text-center',
  wide: 'flex items-center gap-3.5',
};

export function Ring({ percent, display, label, detail, layout = 'auto', className }: RingProps) {
  const p = Math.max(0, Math.min(100, percent));
  return (
    <div className={cx(ROOT[layout], className)}>
      <span className="relative grid size-20 shrink-0 place-items-center" aria-hidden="true">
        <svg viewBox="0 0 84 84" className="absolute inset-0 size-full -rotate-90">
          <circle cx="42" cy="42" r={R} fill="none" strokeWidth="9" className="stroke-jc-elevated" />
          <circle
            cx="42"
            cy="42"
            r={R}
            fill="none"
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={`${(CIRCUMFERENCE * p) / 100} ${CIRCUMFERENCE}`}
            className="stroke-jc-ink"
          />
        </svg>
        <span className="relative type-figure-sm text-jc-ink">{display}</span>
      </span>
      <span className="flex flex-col">
        <span className="type-subheading text-jc-ink">{label}</span>
        {detail ? <span className="type-meta text-jc-ink-secondary">{detail}</span> : null}
      </span>
    </div>
  );
}
